import React from 'react';
import { Check } from 'lucide-react';
import { relocationPages } from '../../data/relocation';
import SEO from '../../components/SEO';

const RelocationVisas = () => {
  const pageData = relocationPages.visas;

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO
        title="Mexico Visa Guide"
        description="Complete guide to Mexico visa options for expats. Temporary resident, permanent resident, digital nomad, and investor visas explained by cross-border experts."
        path="/relocation/cdmx/visa"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F9F6EF] border-b border-[#F4ECD8]">
        <div className="container-wide mx-auto px-8 py-5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-white.png" alt="Mundalo" className="block h-7 w-[140px] object-contain object-left" />
            </a>
            <nav className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm font-medium text-[#1A5C6E] hover:text-[#2CA8A0] transition">Home</a>
              <a href="/relocation/cdmx" className="text-sm font-medium text-[#0A1F2E]">Relocation</a>
              <a href="/contact" className="btn-primary text-xs px-6 py-3">Contact</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="section">
        <div className="container-wide mx-auto">
          <a href="/relocation/cdmx" className="inline-block text-sm font-medium text-[#5A6B73] mb-6 hover:text-[#2CA8A0] transition">
            ← Back to Relocation Hub
          </a>
          <p className="label mb-4">Relocation Hub</p>
          <h1 className="heading-xl mb-6">{pageData.title}</h1>
          <p className="body-lg max-w-3xl">{pageData.description}</p>
        </div>
      </section>

      {/* Visa Types */}
      <section className="section-sm bg-[#FFFFFF]">
        <div className="container-wide mx-auto">
          <h2 className="heading-lg mb-8 text-center">Visa Options</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {pageData.visaTypes.map((visa, idx) => (
              <div key={idx} className="bg-[#FFFFFF] border border-[#E0D6B8] p-6">
                <h3 className="heading-sm mb-2">{visa.name}</h3>
                <p className="text-sm font-semibold text-[#2CA8A0] mb-4">{visa.duration}</p>
                
                <h4 className="text-sm font-medium mb-2 text-[#0A1F2E]">Requirements:</h4>
                <ul className="space-y-2 mb-4">
                  {visa.requirements.map((req, reqIdx) => (
                    <li key={reqIdx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#2CA8A0] flex-shrink-0 mt-0.5" />
                      <span className="body-sm">{req}</span>
                    </li>
                  ))}
                </ul>
                
                <p className="body-sm text-[#5A6B73] italic">{visa.notes}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section">
        <div className="container-narrow mx-auto">
          <h2 className="heading-lg mb-8 text-center">Application Process</h2>
          
          <div className="space-y-6">
            <div className="border-b border-[#E0D6B8] pb-6">
              <h3 className="heading-sm mb-3">Step 1: Gather Documents</h3>
              <p className="body-md">Collect proof of income (bank statements, employment letter, or pension statements), valid passport, passport photos, and completed application form.</p>
            </div>
            <div className="border-b border-[#E0D6B8] pb-6">
              <h3 className="heading-sm mb-3">Step 2: Schedule Consulate Appointment</h3>
              <p className="body-md">Book appointment at Mexican consulate in your home country. Wait times vary by location-plan 4-8 weeks ahead.</p>
            </div>
            <div className="border-b border-[#E0D6B8] pb-6">
              <h3 className="heading-sm mb-3">Step 3: Attend Interview</h3>
              <p className="body-md">Bring all documents to your appointment. Interview is typically brief. Decision usually same day or within a week.</p>
            </div>
            <div>
              <h3 className="heading-sm mb-3">Step 4: Exchange Visa in Mexico</h3>
              <p className="body-md">Within 30 days of arrival, visit INM (immigration office) to exchange visa sticker for resident card. Bring passport and proof of address.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="section-sm bg-[#FFFFFF]">
        <div className="container-narrow mx-auto text-center">
          <p className="body-sm text-[#5A6B73]">
            Immigration requirements change frequently. This information is for general guidance only. We are not immigration attorneys and do not provide legal advice. We connect clients with qualified immigration professionals.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-sm bg-[#FFFFFF] border-t border-[#F4ECD8]">
        <div className="container-narrow mx-auto text-center">
          <h2 className="heading-lg mb-4">Need Immigration Support?</h2>
          <p className="body-md mb-8">
            Our relocation plans include visa guidance and introductions to immigration attorneys.
          </p>
          <a href="/relocation/blueprint" className="btn-primary">View Relocation Plans</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="section-sm border-t border-[#F4ECD8]">
        <div className="container-wide mx-auto text-center">
          <img src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-white.png" alt="Mundalo" className="block h-7 w-[140px] object-contain object-left" />
          <p className="body-sm">© {new Date().getFullYear()} Mundalo. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default RelocationVisas;
